import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { ConfirmationDialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import NotificationMessage from '@ge/feat-monitor/components/notification-message';
import {
  DateTimeFormats,
  FormMode,
  EntityType,
  Capability,
  TaskStatus,
  NotificationType,
} from '@ge/models/constants';
import ActionsMenu from '@ge/shared/components/actions-menu';
import { AuthRender } from '@ge/shared/components/auth-render';
import { DelayDialog } from '@ge/shared/components/tasks/task-delay-dialog';
import { useActionsMenu, useEditTask } from '@ge/shared/data-hooks';
import { useLogger } from '@ge/shared/hooks';
import { ActionKeys, DelayMenuItems } from '@ge/shared/models/actions-menu-items';
import { isActiveDelays, getDelayDuration } from '@ge/shared/util/time-date';
import { typography } from '@ge/tokens';

import TabDetailHeader from '../components/tab-detail-header';
import { TabDetailContainer } from '../entity-details-shared';

import active_delay from './active_delay.svg';
import inactive_delay from './inactive-delay.svg';
import TaskDelayHeader from './task-delay-header';

const DelayHeaderContainer = styled.div`
  padding: 0 10px 15px 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DelayHeader = styled.h3`
  color: ${({ theme }) => theme.entityDetails.cases.details.dataTitle};
  font-size: 14px;
  font-weight: ${typography.medium};
  line-height: 18px;
  text-align: left;
  img {
    margin-right: 5px;
  }
  span {
    color: ${({ theme }) => theme.entityDetails.cases.details.dataDefinition};
  }
`;

const DelayEnd = styled.span`
  color: ${(props) => props.theme.delaysModal.color};
  font-size: 12px;
  font-weight: 400;
  line-height: 14.4px;
  text-align: left;
`;

const DelayRow = styled.div`
  padding: 10px 10px 12px 20px;
  display: flex;
  max-width: 1200px;
  margin: auto;
  overflow: hidden;
  margin: 0 15px 0 0;
`;

const StyledNote = styled.div`
  letter-spacing: 0.5px;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  margin: 7px 0 5px;
  font-weight: 200;
`;

const Column = styled.div`
  display: flex;
  position: relative;
  padding-bottom: 15px;
  flex-direction: column;

  &.small {
    flex: 1;
  }

  &.border {
    border-bottom: 1px solid ${(props) => props.theme.delaysModal.border};
  }

  &.large {
    flex: 3;
  }

  &.endRow {
    text-align: right;
  }

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #ccc;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const Text = styled.div`
  font-family: Museo Sans;
  font-size: 11px;
  font-weight: 400;
  line-height: 13.2px;
  text-align: left;
  color: ${({ theme }) => theme.entityDetails.cases.details.dataTitle};
  &.small {
    margin-top: 1px;
    margin-bottom: 6px;
    line-height: 18px;
    color: ${({ theme }) => theme.entityDetails.cases.details.dataDefinition};
  }
`;

const LoaderContainer = styled.div`
  position: relative;
  margin: 0;
  width: 100%;
  height: 50vh;
`;

export const TaskDelays = ({ task }) => {
  const { t } = useTranslation(['entity-details']);
  const containerRef = useRef(null);
  const { delayDetails, site } = task;
  const logger = useLogger();
  const { notify, dismiss } = useNotification();
  const [showAddDelay, setShowAddDelay] = useState(false);
  const [mode, setMode] = useState(FormMode.CREATE);
  const [currentDelay, setCurrentDelay] = useState(null);
  const [filteredDelays, setFilteredDelays] = useState([]);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
  const [dataToDelete, setDataToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(null);

  const isDisabledReopenTask = task.status === TaskStatus.COMPLETED;

  useEffect(() => {
    setFilteredDelays([]);
    if (delayDetails?.length) {
      const delays = delayDetails
        .map((e, i) => {
          return {
            ...e,
            index: i,
            isActive: isActiveDelays(e.endDate),
          };
        })
        .sort(function (a, b) {
          return new Date(b.addedOn) - new Date(a.addedOn);
        });
      setFilteredDelays(delays);
    }
  }, [delayDetails]);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      taskDelayReason: '',
      taskDelayTypes: '',
      delayStartDate: '',
      delayEndDate: '',
      delayNote: '',
    },
  });

  const saveDelay = useCallback(() => {
    setShowAddDelay(false);
  }, []);

  const { edit: del } = useEditTask({
    onSettled: () => {
      setIsLoading(false);
    },
    onSuccess: () => {
      dismiss();
      let message = (
        <NotificationMessage
          message={t('notes.delays.delay_success', 'DELAY DELETED SUCCESSFULLY')}
        />
      );
      notify({ message });
      setIsLoading(false);
    },
    onError: (e) => {
      setIsLoading(false);
      const message = (
        <NotificationMessage
          message={t('action_buttons.delay_delete_failed', 'DELAY DELETE FAILED')}
        />
      );
      const type = NotificationType.FAILURE;
      notify({ message, type });
      logger.error('Error deleting delay', e);
    },
  });

  const handleCloseDelayDialog = useCallback(() => setShowAddDelay(false), [setShowAddDelay]);

  const handleEditDelay = (delay) => {
    let setData = {};
    if (delay) {
      let startDate = dayjs.utc(delay?.startDate).startOf('day').toDate();
      setData = {
        taskDelayReason: delay?.reason,
        taskDelayTypes: delay?.type,
        delayStartDate: delay?.startDate ? startDate : null,
        delayEndDate: delay?.endDate && new Date(delay?.endDate),
        delayNote: delay?.notes,
      };
    } else {
      setData = {};
    }
    methods.reset(setData);
    setShowAddDelay(true);
    setCurrentDelay({ delay, index: delay.index });
    setMode(FormMode.EDIT);
  };

  const handleAddDelay = useCallback(() => {
    methods.reset({
      taskDelayReason: '',
      taskDelayTypes: '',
      delayStartDate: new Date(),
      delayEndDate: '',
      delayNote: '',
    });
    setShowAddDelay(true);
    setMode(FormMode.CREATE);
  }, [methods]);

  const getAuthorizedMenuItems = useActionsMenu({ menuItems: DelayMenuItems });

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirmationDialog(false);
    setDataToDelete(null);
  }, [setShowDeleteConfirmationDialog, setDataToDelete]);

  const handleDeleteConfirm = useCallback(() => {
    if (dataToDelete) {
      const id = task?.id;
      const delays = [...task.delayDetails];
      delays.splice(dataToDelete.index, 1);
      const payload = {
        properties: {
          delayDetails: [...delays],
        },
      };
      setIsLoading(true);
      del({ id, task: payload, bundledTaskIds: task?.bundledTasks });
      setShowDeleteConfirmationDialog(false);
      setDataToDelete(null);
    }
  }, [del, setIsLoading, dataToDelete, setShowDeleteConfirmationDialog, setDataToDelete]);

  const deleteDelay = useCallback(
    (data) => {
      setDataToDelete(data);
      setShowDeleteConfirmationDialog(true);
    },
    [setDataToDelete, setShowDeleteConfirmationDialog],
  );

  const actionMenuHandler = useCallback(
    (action, data) => {
      if (action === ActionKeys.NOTE_EDIT) handleEditDelay(data);
      else if (action === ActionKeys.NOTE_DELETE) deleteDelay(data);
    },
    [handleEditDelay, deleteDelay],
  );

  const generateDelayCard = (delay) => {
    const { reasonDesc, type, notes, endDate, startDate, addedOn, contributedByName, isActive } =
      delay;
    const formatedDate = dayjs(endDate).format(DateTimeFormats.NEW_TASK);
    const endDateNew = dayjs(formatedDate).isValid() ? formatedDate : null;
    const startDateNew = dayjs.utc(startDate).format(DateTimeFormats.NEW_TASK);
    const reason = reasonDesc?.toUpperCase();
    const typeUpper = type && `- ${type?.toUpperCase()}`;
    const createdOn = dayjs(addedOn).format(DateTimeFormats.DELAY_DATE);
    const delayDur = getDelayDuration(t, delay);
    return (
      <DelayRow key={delay.index}>
        <Column className="small">
          <Text>{t('notes.contributed_by', 'Contributed By')}</Text>
          <Text className="small">{contributedByName || ''}</Text>
          <Text>{t('notes.created_on', 'Created On')}</Text>
          <Text className="small">{createdOn}</Text>
        </Column>
        <Column className="large border">
          <DelayHeader>
            <img src={isActive ? active_delay : inactive_delay} alt="active_delay " />
            <span>
              {reason} {typeUpper}
            </span>
          </DelayHeader>
          <StyledNote>{notes}</StyledNote>
          <DelayEnd>
            {startDateNew}
            {endDateNew && ` - ${endDateNew}`} {delayDur && `| ${delayDur}`}
          </DelayEnd>
        </Column>
        <Column className="small border endRow">
          <AuthRender
            capability={Capability.FIELD_TASKS}
            edit
            delete
            description="Notes and attachments table action menu"
            siteLevel={false}
          >
            <div className="actions">
              <ActionsMenu
                containerRef={containerRef}
                entity={delay}
                menuItems={getAuthorizedMenuItems([delay])}
                menuIconSmall
                onAction={({ type }) => actionMenuHandler(type, delay)}
              />
            </div>
          </AuthRender>
        </Column>
      </DelayRow>
    );
  };

  const delayCardWrapper = () => filteredDelays.map((e, i) => generateDelayCard(e, i));
  return (
    <TabDetailContainer>
      <TabDetailHeader entity={task} entityType={EntityType.TASK} />
      <DelayHeaderContainer>
        <TaskDelayHeader
          handleChange={handleAddDelay}
          delayDetails={filteredDelays}
          getDelayDuration={getDelayDuration}
          isDisabledReopenTask={isDisabledReopenTask}
        />
      </DelayHeaderContainer>

      {isLoading ? (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      ) : (
        <>{filteredDelays?.length ? delayCardWrapper() : ''}</>
      )}
      {showAddDelay && (
        <FormProvider {...methods}>
          <DelayDialog
            task={task}
            onConfirm={saveDelay}
            onClose={handleCloseDelayDialog}
            methods={methods}
            mode={mode}
            currentDelay={currentDelay}
          />
        </FormProvider>
      )}
      <ConfirmationDialog
        contentWidth
        header={t('notes.delays.delete_header', 'Delete Delay Conformation')}
        isOpen={showDeleteConfirmationDialog}
        padContent={true}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmLabel={t('confirm_delete', 'Delete Delay')}
        cancelLabel={t('confirm_delete_cancel', 'Cancel')}
      >
        <p>
          {t(
            'notes.delays.confirm_delete_message',
            'Are you sure you want to permanently delete this Delay?',
          )}
        </p>
      </ConfirmationDialog>
    </TabDetailContainer>
  );
};
export default TaskDelays;

TaskDelays.propTypes = {
  task: PropTypes.instanceOf(Object).isRequired,
};
